/**
 * Recruitment Source Constants
 *
 * Defines the characteristics, stat modifiers, and archetypes for each
 * recruitment source in the game. Used by the candidate generator to
 * create realistic candidates with distinct profiles based on their background.
 */

import type { RecruitmentSource } from '@/types'
import type { IdeologyAxisKey } from './ideology'

/**
 * Stat modifier definition for candidate generation.
 * Uses base value with variance for normal-ish distribution.
 */
export interface StatModifier {
  base: number
  variance: number
}

/**
 * Age range for candidates from a recruitment source.
 */
export interface AgeRange {
  min: number
  max: number
}

/**
 * Complete archetype definition for a recruitment source.
 * Includes stat modifiers, ideology behavior, and metadata.
 */
export interface SourceArchetype {
  description: string
  ageRange: AgeRange
  statModifiers: {
    loyalty: StatModifier
    recognition: StatModifier
    charisma: StatModifier
    admin_skill: StatModifier
    debate_skill: StatModifier
    scandal_prone: StatModifier
    ambition: StatModifier
  }
  /** How much candidate ideology can deviate from party */
  ideologyVariance: number
  /** Bias applied to specific ideology axes (added after variance) */
  ideologyBias?: Partial<Record<IdeologyAxisKey, number>>
}

/**
 * Source archetypes define the statistical profile of candidates
 * from each recruitment source. These affect the generated stats
 * and ideology of recruited candidates.
 */
export const SOURCE_ARCHETYPES: Record<RecruitmentSource, SourceArchetype> = {
  youth_wing: {
    description: 'Young, idealistic party loyalists. Enthusiastic but inexperienced.',
    ageRange: { min: 18, max: 32 },
    statModifiers: {
      loyalty: { base: 80, variance: 15 },
      recognition: { base: 5, variance: 5 },
      charisma: { base: 55, variance: 20 },
      admin_skill: { base: 35, variance: 15 },
      debate_skill: { base: 45, variance: 20 },
      scandal_prone: { base: 25, variance: 15 },
      ambition: { base: 65, variance: 20 },
    },
    ideologyVariance: 10,
  },

  union: {
    description: 'Labor movement veterans. Strong with working-class voters.',
    ageRange: { min: 28, max: 58 },
    statModifiers: {
      loyalty: { base: 65, variance: 15 },
      recognition: { base: 25, variance: 15 },
      charisma: { base: 60, variance: 15 },
      admin_skill: { base: 55, variance: 15 },
      debate_skill: { base: 60, variance: 15 },
      scandal_prone: { base: 35, variance: 15 },
      ambition: { base: 50, variance: 20 },
    },
    ideologyVariance: 15,
    ideologyBias: { economic_regulation: 20 },
  },

  business: {
    description: 'Business leaders and entrepreneurs. Fundraising connections.',
    ageRange: { min: 32, max: 62 },
    statModifiers: {
      loyalty: { base: 50, variance: 20 },
      recognition: { base: 30, variance: 20 },
      charisma: { base: 55, variance: 20 },
      admin_skill: { base: 70, variance: 15 },
      debate_skill: { base: 50, variance: 15 },
      scandal_prone: { base: 45, variance: 20 },
      ambition: { base: 70, variance: 15 },
    },
    ideologyVariance: 20,
    ideologyBias: { economic_regulation: -25 },
  },

  activist: {
    description: 'Passionate advocates and organizers. High energy, volatile.',
    ageRange: { min: 22, max: 45 },
    statModifiers: {
      loyalty: { base: 70, variance: 25 },
      recognition: { base: 20, variance: 15 },
      charisma: { base: 70, variance: 15 },
      admin_skill: { base: 40, variance: 15 },
      debate_skill: { base: 65, variance: 15 },
      scandal_prone: { base: 50, variance: 20 },
      ambition: { base: 55, variance: 20 },
    },
    ideologyVariance: 25,
    ideologyBias: { social_values: 15, civil_liberties: 10 },
  },

  think_tank: {
    description: 'Policy experts and intellectuals. Knowledgeable but may lack charisma.',
    ageRange: { min: 30, max: 55 },
    statModifiers: {
      loyalty: { base: 55, variance: 15 },
      recognition: { base: 15, variance: 10 },
      charisma: { base: 40, variance: 20 },
      admin_skill: { base: 75, variance: 10 },
      debate_skill: { base: 65, variance: 15 },
      scandal_prone: { base: 20, variance: 10 },
      ambition: { base: 45, variance: 20 },
    },
    ideologyVariance: 15,
  },

  independent: {
    description:
      'Experienced politicians without party history. Skilled but may have baggage.',
    ageRange: { min: 35, max: 65 },
    statModifiers: {
      loyalty: { base: 45, variance: 20 },
      recognition: { base: 45, variance: 25 },
      charisma: { base: 60, variance: 20 },
      admin_skill: { base: 60, variance: 20 },
      debate_skill: { base: 60, variance: 20 },
      scandal_prone: { base: 50, variance: 25 },
      ambition: { base: 65, variance: 20 },
    },
    ideologyVariance: 30,
  },

  poached: {
    description: 'Recruited from rival parties. Experienced but loyalty concerns.',
    ageRange: { min: 30, max: 60 },
    statModifiers: {
      loyalty: { base: 35, variance: 20 },
      recognition: { base: 40, variance: 20 },
      charisma: { base: 55, variance: 20 },
      admin_skill: { base: 55, variance: 20 },
      debate_skill: { base: 55, variance: 20 },
      scandal_prone: { base: 40, variance: 20 },
      ambition: { base: 70, variance: 15 },
    },
    ideologyVariance: 20,
  },
}

/**
 * Human-readable information about each recruitment source.
 * Used for UI display in the recruitment interface.
 */
export const RECRUITMENT_SOURCE_INFO: Record<
  RecruitmentSource,
  {
    name: string
    description: string
    strengths: string[]
    weaknesses: string[]
  }
> = {
  youth_wing: {
    name: 'Youth Wing',
    description: 'Young, idealistic party loyalists',
    strengths: ['High loyalty', 'Low scandal risk', 'Eager to learn'],
    weaknesses: ['Inexperienced', 'Low recognition', 'Unproven'],
  },
  union: {
    name: 'Labor Union',
    description: 'Veteran labor organizers',
    strengths: ['Good with workers', 'Debate skills', 'Organized'],
    weaknesses: ['Ideologically rigid', 'May alienate business'],
  },
  business: {
    name: 'Business',
    description: 'Business leaders and entrepreneurs',
    strengths: ['Strong administration', 'Fundraising connections', 'Ambitious'],
    weaknesses: ['Conditional loyalty', 'Scandal risk from business dealings'],
  },
  activist: {
    name: 'Activist',
    description: 'Passionate advocates and organizers',
    strengths: ['Very charismatic', 'Energetic', 'Strong debaters'],
    weaknesses: ['May go off-script', 'Volatile', 'Weak administration'],
  },
  think_tank: {
    name: 'Think Tank',
    description: 'Policy experts and intellectuals',
    strengths: ['Excellent admin skills', 'Policy expertise', 'Low scandal risk'],
    weaknesses: ['May lack charisma', 'Low recognition', 'Intellectually independent'],
  },
  independent: {
    name: 'Independent',
    description: 'Experienced politicians without party history',
    strengths: ['Already known', 'Well-rounded skills', 'Experienced'],
    weaknesses: ['Proven they will leave parties', 'Unknown history', 'Ambitious'],
  },
  poached: {
    name: 'Poached',
    description: 'Recruited from rival parties',
    strengths: ['Known quantity', 'Experienced', 'Immediate recognition'],
    weaknesses: ['Already defected once', 'Loyalty concerns', 'Very ambitious'],
  },
}
